/* Built In Imports */
import { forwardRef, useEffect, useState } from 'react';

/* External Imports */
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  Grid,
  Link,
  ListItem,
  Radio,
  RadioGroup,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  UnorderedList,
  useMultiStyleConfig,
  useTab,
} from '@chakra-ui/react';
import { render } from 'datocms-structured-text-to-html-string';
import parser from 'html-react-parser';
import { nanoid } from 'nanoid';
import { useRouter } from 'next/router';

/* Internal Imports */
/* Components */
import SingleImage from '@components/Card/SingleImage';
import SectionTitle from '@components/Headings/SectionTitle';
import LazyLoadImageComponent from '@components/UI/LazyLoadImageComponent';
import SingleLineText from '@components/UI/SingleLineText';
import StructuredTextParser from '@components/Utility/ArticleParser/StructuredTextParser';
import { refineUrlDomain } from '@components/Utility/utils';

/* Services */
import { searchByFieldId } from '@components/Utility/Shared/SharedService';

/**
 *
 * @param {object} section
 * @returns ProgramDetails
 */
export const pageCss = {
  en: {
    tabTitle: {
      fontSize: {
        base: '16px',
        md: '22px',
      },
      color: 'red',
    },
    testimonialHeight: 571,
    cardTitle: {
      lineHeight: {
        base: '1.5rem',
        md: '1.6rem',
      },
    },
  },
};
let isViewGtmFired = false;

const ProgramDetailsToronto = ({
  section,
  region,
  lang,
  // pageData,
  // setCityId,
}) => {
  const sectionContent = section?.sectionContent;

  const [noUrl, setNoUrl] = useState('');
  const [yesUrl, setYesUrl] = useState('');
  const [isIeDone, setIsIeDone] = useState(true);
  const [selectedProg, setSelectedProg] = useState(null);
  const [IESelectedProg, setIESelectedProg] = useState('Online');
  const router = useRouter();
  const torontoUrl = router.asPath.includes('-toronto');

  const handleIEProgramRadio = (event, data) => {
    event.preventDefault();
    setIESelectedProg(data.type);
    emptySelection();
  };

  const handleRadioClick = (event, data) => {
    event.preventDefault();
    if (data?.Status?.toLowerCase() !== 'full') {
      setYesUrl(data.Category);
      setNoUrl(data.Category);
      setSelectedProg(data);
    }
  };

  const emptySelection = () => {
    setYesUrl('');
    setNoUrl('');
    setSelectedProg(null);
  };

  useEffect(() => {
    if (typeof window !== undefined) {
      window.addEventListener('scroll', addViewGtm);
      return () => window.removeEventListener('scroll', addViewGtm);
    }
  }, []);

  const addViewGtm = () => {
    if (!isViewGtmFired) {
      const completedIE = document?.getElementById('have-you-completed');
      const rect = completedIE.getBoundingClientRect();
      const rectNum = Number(rect.bottom.toFixed());

      if (rectNum > 0 && rectNum < 180) {
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'view_item',
          ecommerce: {
            currency: '',
            value: '',
            items: [
              {
                item_id: '', // id of the program
                item_name: 'Ecstasy of Enlightenment', // Name of the program (Ex - EoE)
                affiliation: '',
                coupon: '',
                discount: '',
                index: '',
                item_brand: '', //IE Status <Yes/No>
                item_category: '', //IE Lang (User selected IE Language Ex- MR,EN,HI)
                item_category2: '',
                item_category3: '',
                item_category4: '',
                item_category5: '',
                item_list_id: '',
                item_list_name: '',
                item_variant: '',
                location_id: '',
                price: '',
                quantity: '',
              },
            ],
          },
        });
        isViewGtmFired = true;
        setTimeout(() => {
          isViewGtmFired = false;
        }, 4000);
      }
    }
  };

  const gtmClick = () => {
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];

      window.dataLayer.push({
        event: 'EoE_Register_Now',
        IE_status: isIeDone ? 'Yes' : 'No',
        IE_Lang: 'en',
        city: 'Toronto',
      });

      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          currency: '',
          value: '',
          items: [
            {
              item_id: selectedProg?.Category, // id of the program
              item_name: 'Ecstasy of Enlightenment', // Name of the program (Ex - EoE)
              affiliation: '',
              coupon: '',
              discount: '',
              index: '',
              item_brand: isIeDone ? 'Yes' : 'No', //IE Status <Yes/No>
              item_category: 'EN', //IE Lang (User selected IE Language Ex- MR,EN,HI)
              item_category2: '',
              item_category3: '',
              item_category4: '',
              item_category5: '',
              item_list_id: '',
              item_list_name: '',
              item_variant: '',
              location_id: 'Toronto',
              price: '',
              quantity: '',
            },
          ],
        },
      });
    }
  };

  const cardStyle = {
    Open: {
      boxShadow: '0 0 2px #979797',
      background: '#FFFFFF',
    },
    Selected: {
      boxShadow:
        '0px 0px 0px 2px #be3a1d,3.06087px 18.7518px 41px rgba(34, 30, 2, 0.08), 1.98389px 12.154px 24.0116px rgba(34, 30, 2, 0.0607407), 1.179px 7.22293px 13.0593px rgba(34, 30, 2, 0.0485926), 0.612173px 3.75037px 6.6625px rgba(34, 30, 2, 0.04), 0.249404px 1.52793px 3.34074px rgba(34, 30, 2, 0.0314074), 0.0566827px 0.347256px 1.61343px rgba(34, 30, 2, 0.0192593)',
      background: '#F5F0E5',
    },
    Full: {
      outline: 'none',
      boxShadow: 'none',
      background: '#F5F0E5',
    },
  };

  const CustomTab = forwardRef((props, ref) => {
    // 1. Reuse the `useTab` hook
    const tabProps = useTab({ ...props, ref });
    const isSelected = !!tabProps['aria-selected'];

    // 2. Hook into the Tabs `size`, `variant`, props
    const styles = useMultiStyleConfig('Tabs', tabProps);
    const Arrow = () => {
      return (
        <Box
          as="span"
          position="absolute"
          width="20px"
          display={isSelected ? 'block' : 'none'}
          height="11px"
          clipPath="polygon(0 0, 50% 100%, 100% 0)"
          left="50%"
          bottom="-10px"
          background="#FBAD2E"
          style={{ transition: 'width 1s' }}
          zIndex="0"
          transition="0.4s linear"
        ></Box>
      );
    };

    return (
      <Button
        __css={styles.tab}
        {...tabProps}
        width="50%"
        color={isSelected ? '#211637 !important' : '#7B7B7B  !important'}
        bg={isSelected ? '#FBAD2E' : '#E8DECB'}
        border="0"
        fontFamily="FedraSansStd-book"
        fontStyle="normal"
        fontWeight="600"
        fontSize={pageCss[lang]?.tabTitle?.fontSize}
        lineHeight="150.3%"
        _active={{ bg: '#FBAD2E', border: '0' }}
        _focus={{ bg: '#FBAD2E', border: '0' }}
        pos="relative"
        height={{ base: '45px', sm: '81px' }}
        p={{ base: '0', sm: '1rem' }}
      >
        {isSelected ? <Arrow /> : ''}

        {tabProps.children}
      </Button>
    );
  });

  return (
    <Box backgroundColor="#F1EBDF" pt={{ base: '30px', lg: '80px' }}>
      <SectionTitle titleObj={sectionContent[0]} mt="0" />
      <Flex
        justifyContent="center"
        alignItems="baseline"
        flexWrap="wrap"
        px={{ base: '20px', lg: '0' }}
      >
        {searchByFieldId(sectionContent, 'loc-dates') &&
          searchByFieldId(sectionContent, 'loc-dates-mob') && (
            <>
              <Box
                width={{ base: '100%', md: '40%' }}
                display={{ base: 'none', md: 'block' }}
              >
                <StructuredTextParser
                  str={render(
                    searchByFieldId(sectionContent, 'loc-dates').body
                  )}
                  region={region}
                  lang={lang}
                  fontFamily="FedraSansStd-medium"
                  pb="20px"
                  textAlign="center"
                  className="loc-details"
                  color="#CF4520"
                  fontSize="20px"
                />
              </Box>
              <Box
                width={{ base: '100%', md: '30%' }}
                display={{ base: 'block', md: 'none' }}
              >
                <StructuredTextParser
                  str={render(
                    searchByFieldId(sectionContent, 'loc-dates-mob').body
                  )}
                  region={region}
                  lang={lang}
                  fontFamily="FedraSansStd-medium"
                  pb="20px"
                  textAlign="center"
                  className="loc-details"
                  color="#CF4520"
                  fontSize="20px"
                />
              </Box>
              <style>
                {`
                .loc-details {
                  font-family: "FedraSansStd-book"
                }
                .loc-details strong, .loc-details mark {
                  color: black;
                  font-family:"FedraSansStd-medium";
                  font-weight:500
                }
                .loc-details mark {
                  color: #CF4520;
                  background: none;
                }                
              `}
              </style>
            </>
          )}
      </Flex>
      <Flex
        justifyContent="center"
        alignItems="baseline"
        flexWrap="wrap"
        px={{ base: '20px', lg: '0px' }}
        mx="auto"
      >
        <Box width={{ base: '100%', sm: '22%', xl: '22%' }}>
          {sectionContent[1]?.text && (
            <Box display="flex" alignItems="start" gridGap="10px">
              <LazyLoadImageComponent
                src="https://static.sadhguru.org/d/46272/1697477131-icon_calendar.svg"
                my="12px"
                alt="calendar"
              />
              <SingleLineText
                sectionContent={sectionContent[1]}
                fontFamily="FedraSansStd-book"
                my="10px"
                color="#000"
              />
            </Box>
          )}
          {sectionContent[2]?.text && (
            <Box display="flex" alignItems="start" gridGap="10px">
              <LazyLoadImageComponent
                src="https://static.sadhguru.org/d/46272/1697477138-icon_clock_watch.svg"
                my="12px"
                alt="time"
              />

              <SingleLineText
                sectionContent={sectionContent[2]}
                fontFamily="FedraSansStd-book"
                my="10px"
                color="#000"
              />
            </Box>
          )}
        </Box>
        <Box width={{ base: '100%', sm: '22%', xl: '22%' }}>
          {sectionContent[3]?.text && (
            <Box display="flex" alignItems="start" gridGap="10px">
              <LazyLoadImageComponent
                src="https://static.sadhguru.org/d/46272/1697477136-icon_translation.svg"
                my="12px"
                alt="language"
              />
              <SingleLineText
                sectionContent={sectionContent[3]}
                my="10px"
                fontFamily="FedraSansStd-book"
                color="#000"
              />
            </Box>
          )}
          {sectionContent[4]?.text && (
            <Box display="flex" alignItems="start" gridGap="10px">
              <LazyLoadImageComponent
                my="12px"
                alt="location"
                src="https://static.sadhguru.org/d/46272/1697477134-icon_pin_location.svg"
              />
              <SingleLineText
                sectionContent={sectionContent[4]}
                fontFamily="FedraSansStd-book"
                my="10px"
                color="#000"
              />
            </Box>
          )}
        </Box>
      </Flex>
      <Box
        background=" url(https://static.sadhguru.org/d/46272/1694600647-26-3.png) right center no-repeat"
        mt="70px"
      >
        <LazyLoadImageComponent
          width={{ base: '140px', md: '230px' }}
          src="https://static.sadhguru.org/d/46272/1694600647-26-3.png"
          alt="bg"
          position="absolute"
          transform="rotate(180deg)"
        />

        <SectionTitle titleObj={sectionContent[5]} />
        <Box
          display="flex"
          flexDir={{ base: 'column', md: 'row' }}
          maxW="850px"
          py="60px"
          pt="0"
          px={{ base: '15px', md: 0 }}
          w="full"
          mx="auto"
          gridGap={{ base: '25px', md: '55px' }}
          alignItems="center"
        >
          <Box zIndex={3}>
            <LazyLoadImageComponent
              src={sectionContent[6].image?.url}
              alt={sectionContent[6].image?.alt}
            />
          </Box>
          <Box
            width={{ base: 'full', md: '400px' }}
            pt={{ base: '0', md: '40px' }}
          >
            {sectionContent[6]?.text && (
              <Box>
                {' '}
                <SingleLineText
                  sectionContent={sectionContent[6]}
                  fontSize="30px"
                  color="#02026C"
                  lineHeight="normal"
                  fontFamily="FedraSansStd-medium"
                  paddingTop="0"
                />
              </Box>
            )}

            <Box>
              {searchByFieldId(sectionContent, 'ie-contact-us') && (
                <>
                  <StructuredTextParser
                    str={render(
                      searchByFieldId(sectionContent, 'ie-contact-us').body
                    )}
                    region={region}
                    lang={lang}
                    fontSize="20px"
                    color="#000000"
                    textAlign={{ base: 'center', md: 'left' }}
                    fontFamily="FedraSansStd-book"
                    mt="0px"
                    className="linkText"
                  />
                  <style>
                    {`
                      .linkText p a {
                        text-decoration: underline;
                      }
                    `}
                  </style>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box w="100%" maxW="1170px" mx="auto" id="have-you-completed">
        {(sectionContent[9]) && (<>
        <Box maxW="494px" m="20px auto">
          {searchByFieldId(sectionContent, 'completed-ie') && (
            <SingleLineText
              sectionContent={searchByFieldId(sectionContent, 'completed-ie')}
              fontSize={{ base: '24px', md: '30px' }}
              color="#02026C"
              lineHeight="normal"
              fontFamily="FedraSansStd-medium"
              paddingTop="0"
              textAlign="center"
            />
          )}
        </Box>
        <Tabs
          isFitted
          variant="enclosed"
          p="0"
          className="tabcontent"
          border="none"
          borderRadius="12px"
          paddingBottom="20px"
          id="prgTab"
          defaultIndex={0}
        >
          <TabList borderBottom="none">
            <CustomTab
              style={{ borderRadius: '10px 0 0 0' }}
              onClick={() => {
                emptySelection();
                setIsIeDone(true);
              }}
            >
              {sectionContent[9]?.titleText}
            </CustomTab>
            <CustomTab
              style={{ borderRadius: '0 10px 0 0' }}
              onClick={() => {
                emptySelection();
                setIsIeDone(false);
              }}
            >
              {sectionContent[13]?.titleText}
            </CustomTab>
          </TabList>
          <TabPanels m="0 auto" bg="#fff" mb="0">
            {/* Tab 1 starts here */}
            <TabPanel px={{ base: '15px', md: '0' }} pt="0px" pb="60px">
              {searchByFieldId(sectionContent, 'red-msg-yes') && (
                <>
                  <Box
                    color="#CF4520"
                    fontFamily="FedraSansStd-book"
                    fontSize="18px"
                    py={{ base: '20px', md: '40px' }}
                    fontStyle="italic"
                    textAlign="center"
                    px={{ base: '15px', md: '30px' }}
                    maxW="920px"
                    mx="auto"
                  >
                    {searchByFieldId(sectionContent, 'red-msg-yes').text}
                  </Box>
                  <Divider />
                  <Box maxW="590px" mx="auto" mt="25px">
                    <SingleImage
                      sectionContent={sectionContent[18]}
                      region={region}
                      lang={lang}
                      px="0"
                      style={{
                        textAlign: 'center',
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                      sync={true}
                    />

                    <Grid pt="30px">
                      <FormControl>
                        <RadioGroup
                          value={yesUrl}
                          onChange={event => setYesUrl(event.target.value)}
                        >
                          <Flex
                            placeContent="center"
                            gridGap={{ base: '7px', md: '30px' }}
                            gridRowGap={{ base: '15px', md: '30px' }}
                            mt="10px"
                            maxW="700px"
                            flexWrap="wrap"
                            flexDirection="row"
                          >
                            {sectionContent[19]?.data?.map((data, i) => {
                              return (
                                <Box
                                  as="label"
                                  key={nanoid(4)}
                                  w={{ base: '40%', md: '279px' }}
                                  maxW="279px"
                                  minW={{ base: '47%', md: '160px' }}
                                  paddingLeft={{
                                    base: '12.14px',
                                    md: '21.18px',
                                  }}
                                  minH={{ base: '160px', md: '250px' }}
                                  pr={{ base: '11.47px', md: '20px' }}
                                  pt={{ base: '16.5px', md: '29px' }}
                                  pb={{ base: '19px', md: '27px' }}
                                  style={
                                    yesUrl === data.Category
                                      ? cardStyle.Selected
                                      : data?.Status?.toLowerCase() === 'full'
                                      ? cardStyle.Full
                                      : cardStyle.Open
                                  }
                                  position="relative"
                                  borderRadius="10px"
                                  onClick={event =>
                                    handleRadioClick(event, data)
                                  }
                                  cursor={
                                    data?.Status?.toLowerCase() === 'full'
                                      ? 'cursor'
                                      : 'pointer'
                                  }
                                  _hover={
                                    {
                                      // outline:
                                      //   data.Status !== 'Full' &&
                                      //   '2px solid #CF4520 !Important',
                                      // boxShadow:
                                      //   data.Status !== 'Full' &&
                                      //   '0 0 0px 2px #CF4520, 3.06087px 18.7518px 41px rgba(34, 30, 2, 0.08), 1.98389px 12.154px 24.0116px rgba(34, 30, 2, 0.0607407), 1.179px 7.22293px 13.0593px rgba(34, 30, 2, 0.0485926), 0.612173px 3.75037px 6.6625px rgba(34, 30, 2, 0.04), 0.249404px 1.52793px 3.34074px rgba(34, 30, 2, 0.0314074), 0.0566827px 0.347256px 1.61343px rgba(34, 30, 2, 0.0192593) !Important',
                                      // background:
                                      //   data.Status !== 'Full' &&
                                      //   '#F5F0E5 !Important',
                                    }
                                  }
                                >
                                  {data?.Status?.toLowerCase() === 'full' && (
                                    <Box
                                      pos="absolute"
                                      top={0}
                                      right={0}
                                      left={0}
                                      bottom={0}
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="center"
                                      textAlign="center"
                                      background="#494949"
                                      opacity="0.7"
                                      borderRadius="10px"
                                    >
                                      <Text
                                        color="#fff"
                                        fontSize="24px"
                                        fontFamily="FedraSansStd-medium"
                                      >
                                        FULL
                                      </Text>
                                    </Box>
                                  )}
                                  <style>
                                    {`
                                  .radio{
                                    border:0 !important;
                                    background:transparent !important;
                                  }
                                  .radio:before{
                                    background:#CF4520 !important;
                                  }
                                  `}
                                  </style>
                                  <Flex justifyContent="space-between">
                                    <Box
                                      fontFamily="FedraSansStd-medium"
                                      fontStyle="normal"
                                      fontWeight="500"
                                      fontSize={{ md: '20px', base: '14px' }}
                                      lineHeight="16px"
                                      display="flex"
                                      alignItems="center"
                                      fontFeatureSettings="case"
                                      color="#000000"
                                    >
                                      {data.Category}
                                    </Box>
                                    <Radio
                                      name="inComp"
                                      id="radio1"
                                      className="radio"
                                      colorScheme="orange"
                                      value={data.Category}
                                      boxShadow="0 0 0 1px #CF4520"
                                      width="26px"
                                      height="26px"
                                      key={data.RefName}
                                      cursor="pointer"
                                      isDisabled={
                                        data?.Status?.toLowerCase() === 'full'
                                          ? true
                                          : false
                                      }
                                    />
                                  </Flex>

                                  {/* <Box
                                    maxW={{ base: '110px', md: '150px' }}
                                    fontFamily="FedraSansStd-book"
                                    borderRadius="4px"
                                    fontSize={{ md: '16px', base: '14px' }}
                                    mb="10px"
                                    display="block"
                                    flexWrap="wrap"
                                  ></Box> */}
                                  {data?.EBText && (
                                    <Box
                                      color={'#B40000'}
                                      fontFamily="FedraSansStd-book"
                                      fontSize={{ base: '10.46px', md: '18px' }}
                                    >
                                      {data.EBText}
                                    </Box>
                                  )}
                                  <Box
                                    fontFamily="FedraSansStd-book"
                                    background="#E4DED4"
                                    borderRadius="4px"
                                    p={{ sm: '4px 12px', base: '5px' }}
                                    fontSize={{ md: '18px', base: '10.5px' }}
                                    my="5px"
                                    color="#000"
                                    textAlign="left"
                                    w="auto"
                                    display="inline-flex"
                                  >
                                    {data.NormalPrice !== data.EBPrice && (
                                      <Box
                                        as="span"
                                        textDecoration="line-through"
                                        mr="10px"
                                      >
                                        {data.NormalPrice}
                                      </Box>
                                    )}

                                    {data.EBPrice}
                                  </Box>

                                  <Box>
                                    <UnorderedList
                                      fontStyle="normal"
                                      fontWeight="400"
                                      fontSize={{ md: '16px', base: '10px' }}
                                      lineHeight={{ md: '20px', base: '16px' }}
                                      fontFeatureSettings="case"
                                      color="#28231E"
                                      fontFamily="FedraSansStd-book"
                                    >
                                      {data.Descripton.value_1 && (
                                        <ListItem mb="10px">
                                          {parser(
                                            data.Descripton.value_1 || ''
                                          )}
                                        </ListItem>
                                      )}
                                      {data.Descripton.value_2 && (
                                        <ListItem mb="10px">
                                          {parser(
                                            data.Descripton.value_2 || ''
                                          )}
                                        </ListItem>
                                      )}
                                      {data.Descripton.value_3 && (
                                        <ListItem mb="10px">
                                          {parser(
                                            data.Descripton.value_3 || ''
                                          )}
                                        </ListItem>
                                      )}
                                    </UnorderedList>
                                  </Box>
                                </Box>
                              );
                            })}
                          </Flex>
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                    {searchByFieldId(sectionContent, 'yes-price-include') && (
                      <StructuredTextParser
                        str={render(
                          searchByFieldId(sectionContent, 'yes-price-include')
                            .body
                        )}
                        region={region}
                        lang={lang}
                        fontFamily="FedraSansStd-book"
                        textAlign="center"
                        pt="20px"
                        fontStyle="italic"
                        fontSize="16px"
                        mt="0px"
                      />
                    )}
                  </Box>

                  {sectionContent[17]?._modelApiKey === 'simple_button' && (
                    <Box display="flex" placeContent="center" mt="40px">
                      <Link
                        href={
                          selectedProg?.LinkUrl
                            ? refineUrlDomain(
                                selectedProg?.LinkUrl +
                                  `?seat=${yesUrl}&package=${0}` ||
                                  sectionContent[17]?.linkUrl +
                                    `?seat=${yesUrl}&package=${0}`,
                                region,
                                lang
                              )
                            : 'javascript:void()'
                        }
                        mb="20px"
                        display="block"
                        fontWeight={'500'}
                        fontFamily={'FedraSansStd-medium'}
                        fontSize={'18px'}
                        bgColor={yesUrl ? '#CF4520' : '#333333'}
                        opacity={yesUrl ? '1' : '0.4'}
                        h={'auto'}
                        minH="50px"
                        textAlign="left"
                        padding={'14px 29px'}
                        borderRadius="5px"
                        color="#faf7f0"
                        textDecoration="none"
                        width={'auto'}
                        cursor={yesUrl ? 'pointer' : 'not-allowed'}
                        _hover={{
                          background: yesUrl ? '#000054' : '#333333',
                        }}
                        target={yesUrl ? '_blank' : ''}
                        onClick={() => {
                          gtmClick();
                        }}
                      >
                        {selectedProg?.CTAButton ||
                          sectionContent[17]?.buttonText}
                      </Link>
                    </Box>
                  )}

                  {searchByFieldId(sectionContent, 'no-text-1') && (
                    <StructuredTextParser
                      str={render(
                        searchByFieldId(sectionContent, 'no-text-1').body
                      )}
                      region={region}
                      lang={lang}
                      fontFamily="FedraSansStd-book"
                      fontStyle="italic"
                      pb="0"
                      px="15px"
                      textAlign="center"
                      py="5px"
                    />
                  )}
                </>
              )}
            </TabPanel>

            {/* Tab 2 starts here */}
            <TabPanel px={{ base: '15px', md: '0' }} pt="0px" pb="30px">
              {searchByFieldId(sectionContent, 'red-msg-no') && (
                <Box
                  color="#CF4520"
                  fontFamily="FedraSansStd-book"
                  fontSize={{ base: '14px', md: '18px', lg: '18px' }}
                  fontWeight={600}
                  pt={{ base: '25px', md: '45px', lg: '45px' }}
                  pb="10px"
                  px={{ base: '0px', md: '0px', lg: '30px' }}
                  fontStyle="italic"
                  textAlign="center"
                  maxW={{ base: '100%', md: '80%', lg: '800px' }}
                  mx="auto"
                  lineHeight={'23px'}
                  justifyContent={'center'}
                >
                  {searchByFieldId(sectionContent, 'red-msg-no').text}
                </Box>
              )}
              {torontoUrl &&
                searchByFieldId(sectionContent, 'no-regn-closed') && (
                  <>
                    <Box
                      width={{ base: '100%', md: '60%' }}
                      m={'0 auto'}
                    >
                      <StructuredTextParser
                        str={render(
                          searchByFieldId(sectionContent, 'no-regn-closed').body
                        )}
                        region={region}
                        lang={lang}
                        fontFamily="FedraSansStd-book"
                        textAlign="center"
                        color="#000000"
                        fontSize={{base:'16px', md:'18px'}}
                        className="note_text"
                      />
                    </Box>
                  </>
                )}
              {!torontoUrl && (
                <>
                  {searchByFieldId(sectionContent, 'black-msg-no') && (
                    <Box
                      color="#000000"
                      fontFamily="FedraSansStd-book"
                      fontSize={{ base: '12px', md: '14px', lg: '14px' }}
                      fontWeight={600}
                      pt={{ base: '10px', md: '20px', lg: '20px' }}
                      pb="10px"
                      px={{ base: '15px', md: '0px', lg: '30px' }}
                      // fontStyle="italic"
                      textAlign="center"
                      maxW={{ base: '100%', md: '80%', lg: '720px' }}
                      mx="auto"
                    >
                      {searchByFieldId(sectionContent, 'black-msg-no').text}
                    </Box>
                  )}

                  <Grid pt={{ base: '15px', md: '30px', lg: '30px' }}>
                    <FormControl>
                      <RadioGroup value={IESelectedProg}>
                        <Flex
                          placeContent="center"
                          gridGap={{ base: '7px', md: '30px' }}
                          gridRowGap={{ base: '15px', md: '30px' }}
                          // mt="10px"
                          // maxW="700px"
                          flexWrap="wrap"
                          flexDirection="row"
                          justifyContent={'center'}
                        >
                          {sectionContent[24]?.data?.map((data, i) => {
                            return (
                              <Box
                                as="label"
                                key={nanoid(4)}
                                w={{ base: '40%', md: '279px', lg: '762px' }}
                                minW={{ base: '100%', md: '75%', lg: '160px' }}
                                // minH={{ base: '160px', md: '250px' }}
                                paddingLeft={{
                                  base: '12.14px',
                                  md: '21.18px',
                                }}
                                display={
                                  torontoUrl == true && i == 0
                                    ? 'none'
                                    : 'block'
                                }
                                pr={{ base: '11.47px', md: '20px' }}
                                pt={{ base: '16.5px', md: '20px' }}
                                pb={{ base: '19px', md: '20px' }}
                                height="auto"
                                style={
                                  data?.type === IESelectedProg
                                    ? cardStyle.Selected
                                    : data?.type === IESelectedProg
                                    ? cardStyle.Full
                                    : cardStyle.Open
                                }
                                position="relative"
                                borderRadius="10px"
                                onClick={event =>
                                  handleIEProgramRadio(event, data)
                                }
                                cursor={
                                  data?.type === IESelectedProg
                                    ? 'cursor'
                                    : 'pointer'
                                }
                              >
                                <style>
                                  {`
                                  .radio{
                                    border:0 !important;
                                    background:transparent !important;
                                  }
                                  .radio:before{
                                    background:#CF4520 !important;
                                  }
                                  `}
                                </style>
                                <Flex justifyContent="space-between">
                                  <Box
                                    fontFamily="FedraSansStd-medium"
                                    fontStyle="normal"
                                    fontWeight="500"
                                    fontSize={{ md: '20px', base: '14px' }}
                                    lineHeight="16px"
                                    display="flex"
                                    alignItems="center"
                                    fontFeatureSettings="case"
                                    color="#303030"
                                    marginRight={{ base: '20px', md: '20px' }}
                                  >
                                    {data.Category}
                                  </Box>
                                  <Radio
                                    name="inComp"
                                    id="radio1"
                                    className="radio"
                                    colorScheme="orange"
                                    value={data.type}
                                    boxShadow="0 0 0 1px #CF4520"
                                    width="20px"
                                    height="20px"
                                    key={data.RefName}
                                    cursor="pointer"
                                    isDisabled={
                                      data?.type === IESelectedProg
                                        ? true
                                        : false
                                    }
                                  />
                                </Flex>
                                {data?.EBText && (
                                  <Box
                                    color={'#820000'}
                                    mt={'12px'}
                                    mr={{ base: '20px', md: '100px' }}
                                    fontFamily="FedraSansStd-book"
                                    fontStyle={'italic'}
                                    fontSize={{ base: '10.46px', md: '14px' }}
                                    maxWidth={{
                                      base: '85%',
                                      md: '85%',
                                      lg: '622px',
                                    }}
                                  >
                                    {data.EBText}
                                  </Box>
                                )}

                                <Box
                                  mt={'12px'}
                                  mr={{ base: '20px', md: '100px' }}
                                >
                                  <UnorderedList
                                    fontStyle="normal"
                                    fontWeight="400"
                                    fontSize={{ md: '16px', base: '10px' }}
                                    lineHeight={{ md: '20px', base: '16px' }}
                                    fontFeatureSettings="case"
                                    color="#303030"
                                    fontFamily="FedraSansStd-book"
                                  >
                                    {data.Descripton.value_1 && (
                                      <ListItem
                                        mb={{
                                          base: '0px',
                                          lg: '10px',
                                          md: '10px',
                                        }}
                                      >
                                        {parser(data.Descripton.value_1 || '')}
                                      </ListItem>
                                    )}
                                    {data.Descripton.value_2 && (
                                      <ListItem
                                        mb={{
                                          base: '0px',
                                          lg: '10px',
                                          md: '10px',
                                        }}
                                      >
                                        {parser(data.Descripton.value_2 || '')}
                                      </ListItem>
                                    )}
                                    {data.Descripton.value_3 && (
                                      <ListItem
                                        mb={{
                                          base: '0px',
                                          lg: '10px',
                                          md: '10px',
                                        }}
                                      >
                                        {parser(data.Descripton.value_3 || '')}
                                      </ListItem>
                                    )}
                                    {data.Descripton.value_4 && (
                                      <ListItem
                                        mb={{
                                          base: '0px',
                                          lg: '10px',
                                          md: '10px',
                                        }}
                                      >
                                        {parser(data.Descripton.value_4 || '')}
                                      </ListItem>
                                    )}
                                  </UnorderedList>
                                </Box>
                              </Box>
                            );
                          })}
                        </Flex>
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                </>
              )}

              {!torontoUrl &&
                searchByFieldId(sectionContent, 'select-category-msg-no') && (
                  <Box
                    color="#000000"
                    fontFamily="FedraSansStd-book"
                    fontSize="18px"
                    fontWeight={600}
                    py={{ base: '40px', md: '40px', lg: '20px' }}
                    pb={{ base: '5px', md: '20px', lg: '20px' }}
                    px={{ base: '15px', md: '30px' }}
                    // fontStyle="italic"
                    textAlign="center"
                    maxW="720px"
                    mx="auto"
                  >
                    {
                      searchByFieldId(sectionContent, 'select-category-msg-no')
                        .text
                    }
                  </Box>
                )}

              {/* 
              {searchByFieldId(sectionContent, 'no-text-1') && (
                <StructuredTextParser
                  str={render(
                    searchByFieldId(sectionContent, 'no-text-1').body
                  )}
                  region={region}
                  lang={lang}
                  fontFamily="FedraSansStd-medium"
                  textAlign="center"
                  pt="10px"
                  pb="30px"
                  fontSize="24px"
                  mb="10px"
                />
              )} */}

              {/* <Box borderBottom="1px solid silver" height="1px" mb="20px"></Box> */}

              {!torontoUrl && (
                <>
                  <Box maxW="590px" mx="auto">
                    <SingleImage
                      sectionContent={sectionContent[18]}
                      region={region}
                      lang={lang}
                      px="0"
                      style={{
                        textAlign: 'center',
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                      sync={true}
                    />

                    <Grid pt="30px">
                      <FormControl>
                        <RadioGroup
                          onChange={event => setNoUrl(event.target.value)}
                          value={noUrl}
                        >
                          <Flex
                            placeContent="center"
                            gridGap={{ base: '7px', md: '30px' }}
                            gridRowGap={{ base: '15px', md: '30px' }}
                            mt="10px"
                            maxW="700px"
                            flexWrap="wrap"
                            flexDirection="row"
                          >
                            {sectionContent[12]?.data?.map((data, i) => {
                              if (data?.type === IESelectedProg || !data.type) {
                                return (
                                  <Box
                                    as="label"
                                    key={nanoid(4)}
                                    w={{ base: '40%', md: '279px' }}
                                    maxW="279px"
                                    minW={{ base: '47%', md: '160px' }}
                                    minH={{ base: '160px', md: '250px' }}
                                    paddingLeft={{
                                      base: '12.14px',
                                      md: '21.18px',
                                    }}
                                    pr={{ base: '11.47px', md: '20px' }}
                                    pt={{ base: '16.5px', md: '29px' }}
                                    pb={{ base: '19px', md: '27px' }}
                                    height="auto"
                                    style={
                                      yesUrl === data.Category
                                        ? cardStyle.Selected
                                        : data?.Status?.toLowerCase() === 'full'
                                        ? cardStyle.Full
                                        : cardStyle.Open
                                    }
                                    position="relative"
                                    borderRadius="10px"
                                    onClick={event =>
                                      handleRadioClick(event, data)
                                    }
                                    cursor={
                                      data?.Status?.toLowerCase() === 'full'
                                        ? 'cursor'
                                        : 'pointer'
                                    }
                                    _hover={
                                      {
                                        // outline:
                                        //   data.Status !== 'Full' &&
                                        //   '2px solid #CF4520 !Important',
                                        // boxShadow:
                                        //   data.Status !== 'Full' &&
                                        //   '0 0 0px 2px #CF4520, 3.06087px 18.7518px 41px rgba(34, 30, 2, 0.08), 1.98389px 12.154px 24.0116px rgba(34, 30, 2, 0.0607407), 1.179px 7.22293px 13.0593px rgba(34, 30, 2, 0.0485926), 0.612173px 3.75037px 6.6625px rgba(34, 30, 2, 0.04), 0.249404px 1.52793px 3.34074px rgba(34, 30, 2, 0.0314074), 0.0566827px 0.347256px 1.61343px rgba(34, 30, 2, 0.0192593) !Important',
                                        // background:
                                        //   data.Status !== 'Full' &&
                                        //   '#F5F0E5 !Important',
                                      }
                                    }
                                  >
                                    {data?.Status?.toLowerCase() === 'full' && (
                                      <Box
                                        pos="absolute"
                                        top={0}
                                        right={0}
                                        left={0}
                                        bottom={0}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        textAlign="center"
                                        background="#494949"
                                        opacity="0.7"
                                        borderRadius="10px"
                                      >
                                        <Text
                                          color="#fff"
                                          fontSize="24px"
                                          fontFamily="FedraSansStd-medium"
                                        >
                                          FULL
                                        </Text>
                                      </Box>
                                    )}
                                    <style>
                                      {`
                                      .radio{
                                        border:0 !important;
                                        background:transparent !important;
                                      }
                                      .radio:before{
                                        background:#CF4520 !important;
                                      }
                                      `}
                                    </style>
                                    <Flex justifyContent="space-between">
                                      <Box
                                        fontFamily="FedraSansStd-medium"
                                        fontStyle="normal"
                                        fontWeight="500"
                                        fontSize={{ md: '20px', base: '14px' }}
                                        lineHeight="22px"
                                        display="flex"
                                        alignItems="center"
                                        fontFeatureSettings="case"
                                        color="#000000"
                                        width={'80%'}
                                      >
                                        {data.Category}
                                      </Box>
                                      <Radio
                                        name="inComp"
                                        id="radio1"
                                        className="radio"
                                        colorScheme="orange"
                                        value={data.Category}
                                        boxShadow="0 0 0 1px #CF4520"
                                        width="20px"
                                        height="20px"
                                        key={data.RefName}
                                        cursor="pointer"
                                        isDisabled={
                                          data?.Status?.toLowerCase() === 'full'
                                            ? true
                                            : false
                                        }
                                      />
                                    </Flex>
                                    {/* <Box
                                    maxW={{ base: '110px', md: '150px' }}
                                    fontFamily="FedraSansStd-book"
                                    borderRadius="4px"
                                    fontSize={{ md: '16px', base: '14px' }}
                                    mb="10px"
                                    display="block"
                                    flexWrap="wrap"
                                  ></Box> */}
                                    {data?.EBText && (
                                      <Box
                                        color={'#B40000'}
                                        fontFamily="FedraSansStd-book"
                                        fontSize={{
                                          base: '10.46px',
                                          md: '18px',
                                        }}
                                      >
                                        {data.EBText}
                                      </Box>
                                    )}
                                    <Box
                                      fontFamily="FedraSansStd-book"
                                      background="#E4DED4"
                                      borderRadius="4px"
                                      p={{ sm: '4px 12px', base: '5px' }}
                                      fontSize={{ md: '18px', base: '10.5px' }}
                                      mt="10px"
                                      mb={'20px'}
                                      color="#000"
                                      textAlign="left"
                                      w="auto"
                                      display="inline-flex"
                                    >
                                      {data.NormalPrice !== data.EBPrice && (
                                        <Box
                                          as="span"
                                          textDecoration="line-through"
                                          mr="10px"
                                        >
                                          {data.NormalPrice}
                                        </Box>
                                      )}

                                      {data.EBPrice}
                                    </Box>
                                    <Box>
                                      <UnorderedList
                                        fontStyle="normal"
                                        fontWeight="400"
                                        fontSize={{ md: '16px', base: '10px' }}
                                        lineHeight={{
                                          md: '20px',
                                          base: '16px',
                                        }}
                                        fontFeatureSettings="case"
                                        color="#28231E"
                                        fontFamily="FedraSansStd-book"
                                      >
                                        {data.Descripton.value_1 && (
                                          <ListItem mb="10px">
                                            {parser(
                                              data.Descripton.value_1 || ''
                                            )}
                                          </ListItem>
                                        )}
                                        {data.Descripton.value_2 && (
                                          <ListItem mb="10px">
                                            {parser(
                                              data.Descripton.value_2 || ''
                                            )}
                                          </ListItem>
                                        )}
                                        {data.Descripton.value_3 && (
                                          <ListItem mb="10px">
                                            {parser(
                                              data.Descripton.value_3 || ''
                                            )}
                                          </ListItem>
                                        )}
                                      </UnorderedList>
                                    </Box>
                                  </Box>
                                );
                              }
                            })}
                          </Flex>
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                    {searchByFieldId(sectionContent, 'by-register') && (
                      <StructuredTextParser
                        str={render(
                          searchByFieldId(sectionContent, 'by-register').body
                        )}
                        region={region}
                        lang={lang}
                        fontFamily="FedraSansStd-book"
                        textAlign="center"
                        pt={{ base: '25px', md: '40px', lg: '50px' }}
                        fontSize="16px"
                        mt="5px"
                        color={'#28231E'}
                      />
                    )}
                    {searchByFieldId(sectionContent, 'by-register-2') && (
                      <StructuredTextParser
                        str={render(
                          searchByFieldId(sectionContent, 'by-register-2').body
                        )}
                        region={region}
                        lang={lang}
                        fontStyle={'italic'}
                        fontFamily="FedraSansStd-book"
                        textAlign="center"
                        pt={'20px'}
                        fontSize="16px"
                        mt="5px"
                        color={'#303030'}
                      />
                    )}
                  </Box>

                  {sectionContent[17]?._modelApiKey === 'simple_button' && (
                    <Box
                      display="flex"
                      placeContent="center"
                      mt={{ base: '5px', md: '10px', lg: '10px' }}
                    >
                      <Link
                        href={
                          selectedProg?.LinkUrl
                            ? refineUrlDomain(
                                `${selectedProg?.LinkUrl}?seat=${
                                  noUrl?.trim().split(' ')[0]
                                }&package=${
                                  IESelectedProg === 'In Person' ? 2 : 1
                                }`,
                                region,
                                lang
                              )
                            : 'javascript:void()'
                        }
                        my="30px"
                        display="block"
                        fontWeight={'500'}
                        fontFamily={'FedraSansStd-medium'}
                        fontSize={'18px'}
                        bgColor={noUrl ? '#cf4520' : '#cf452069'}
                        h={'auto'}
                        minH="50px"
                        textAlign="left"
                        padding={'14px 29px'}
                        borderRadius="5px"
                        color="#faf7f0"
                        textDecoration="none"
                        width={'auto'}
                        cursor={noUrl ? 'pointer' : 'not-allowed'}
                        _hover={{
                          bg: noUrl ? '#000054' : '',
                        }}
                        target={noUrl ? '_blank' : ''}
                        onClick={() => {
                          gtmClick();
                        }}
                      >
                        {selectedProg?.CTAButton ||
                          sectionContent[17]?.buttonText}
                      </Link>
                    </Box>
                  )}

                  <Box maxW="590px" m="0 auto">
                    {searchByFieldId(sectionContent, 'no-price-include') && (
                      <StructuredTextParser
                        str={render(
                          searchByFieldId(sectionContent, 'no-price-include')
                            .body
                        )}
                        region={region}
                        lang={lang}
                        fontFamily="FedraSansStd-book"
                        fontStyle="italic"
                        color="#303030"
                        px="15px"
                      />
                    )}

                    {searchByFieldId(sectionContent, 'no-text-1') && (
                      <StructuredTextParser
                        str={render(
                          searchByFieldId(sectionContent, 'no-text-1').body
                        )}
                        region={region}
                        lang={lang}
                        fontFamily="FedraSansStd-book"
                        fontStyle="italic"
                        pb="0"
                        color={'#000000'}
                        px="15px"
                        py="5px"
                      />
                    )}
                  </Box>
                </>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
        </>)}
      </Box>
      <style jsx global>
        {`
          .textParagraph p {
            font-size: 18px;
            color: #be3a1d;
            text-transform: uppercase;
          }
          .note_text a {
            color: #d04723;
            text-decoration: underline;
          }
          .textParagraph p:nth-child(2) {
            font-size: 16px;
            color: #28231e;
            text-transform: none;
            font-family: 'FedraSansStd-book';
          }
          @media only screen and (max-width: 360px) {
            .gridComponent {
              grid-template-columns: repeat(1, minmax(0, 1fr));
              margin-top: 10px;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default ProgramDetailsToronto;
